import React, { createContext, useState, useEffect, useContext } from 'react';
import mockStories from '../data/mockStories';
import mockReviews from '../data/mockReviews';
import mockEvents from '../data/mockEvents';
import { useUser } from './UserContext';

// Create context
export const CommunityContext = createContext();

export const CommunityProvider = ({ children }) => {
  const { currentUser, isAuthenticated } = useUser();
  
  // State for community content
  const [stories, setStories] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [events, setEvents] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Initialize with mock data
  useEffect(() => {
    // In a real app, these would be API calls
    setStories(mockStories);
    setReviews(mockReviews);
    setEvents(mockEvents);
    
    // Create initial posts from stories and reviews
    const initialPosts = [
      ...mockStories.map(story => ({
        id: `story-${story.id}`,
        type: 'story',
        content: story,
        timestamp: new Date(story.date).getTime()
      })),
      ...mockReviews.map(review => ({
        id: `review-${review.id}`,
        type: 'review',
        content: review,
        timestamp: new Date(review.date).getTime()
      }))
    ];
    
    // Sort by timestamp, newest first
    initialPosts.sort((a, b) => b.timestamp - a.timestamp);
    
    setPosts(initialPosts);
    setLoading(false);
  }, []);

  // Add a new story
  const addStory = (storyData) => {
    if (!isAuthenticated) return { success: false, message: 'You must be logged in' };
    
    const newStory = {
      id: Date.now(),
      title: storyData.title,
      author: {
        id: currentUser.id,
        name: currentUser.name,
        avatar: currentUser.avatar
      },
      date: new Date().toISOString().split('T')[0],
      location: storyData.location,
      content: storyData.content,
      images: storyData.images || [],
      tags: storyData.tags || [],
      likes: 0,
      comments: 0,
      visibility: storyData.visibility || 'community'
    };
    
    // Update stories state
    setStories(prevStories => [newStory, ...prevStories]);
    
    // Add to posts feed
    const newPost = {
      id: `story-${newStory.id}`,
      type: 'story',
      content: newStory,
      timestamp: Date.now()
    };
    
    setPosts(prevPosts => [newPost, ...prevPosts]);
    
    return { success: true, story: newStory };
  };

  // Add a new review
  const addReview = (reviewData) => {
    if (!isAuthenticated) return { success: false, message: 'You must be logged in' };
    
    const newReview = {
      id: Date.now(),
      title: reviewData.title,
      author: {
        id: currentUser.id,
        name: currentUser.name,
        avatar: currentUser.avatar
      },
      date: new Date().toISOString().split('T')[0],
      location: reviewData.location,
      venueName: reviewData.venueName,
      venueType: reviewData.venueType,
      rating: reviewData.rating,
      content: reviewData.content,
      images: reviewData.images || [],
      tags: reviewData.tags || [],
      safetyRating: reviewData.safetyRating,
      inclusivityRating: reviewData.inclusivityRating,
      likes: 0,
      helpful: 0,
      visibility: reviewData.visibility || 'community'
    };
    
    // Update reviews state
    setReviews(prevReviews => [newReview, ...prevReviews]);
    
    // Add to posts feed
    const newPost = {
      id: `review-${newReview.id}`,
      type: 'review',
      content: newReview,
      timestamp: Date.now()
    };
    
    setPosts(prevPosts => [newPost, ...prevPosts]);
    
    return { success: true, review: newReview };
  };

  // Create a new event
  const addEvent = (eventData) => {
    if (!isAuthenticated) return { success: false, message: 'You must be logged in' };
    
    const newEvent = {
      id: Date.now(),
      title: eventData.title,
      description: eventData.description,
      location: eventData.location,
      startDate: eventData.startDate,
      endDate: eventData.endDate,
      image: eventData.image,
      category: eventData.category,
      organizer: {
        id: currentUser.id,
        name: currentUser.name
      },
      attendees: 0,
      isFeatured: false,
      tags: eventData.tags || [],
      website: eventData.website || '',
      ticketRequired: eventData.ticketRequired || false,
      price: eventData.price || 'Free',
      accessibility: eventData.accessibility || []
    };
    
    // Update events state
    setEvents(prevEvents => [newEvent, ...prevEvents]);
    
    return { success: true, event: newEvent };
  };

  // Like/unlike a story
  const toggleStoryLike = (storyId) => {
    if (!isAuthenticated) return { success: false, message: 'You must be logged in' };
    
    setStories(prevStories => 
      prevStories.map(story => {
        if (story.id === storyId) {
          // In a real app, we would track which users liked which stories
          // Here we just toggle the count up and down
          return { ...story, likes: story.likes + 1 };
        }
        return story;
      })
    );
    
    // Also update in posts
    setPosts(prevPosts => 
      prevPosts.map(post => {
        if (post.type === 'story' && post.content.id === storyId) {
          return {
            ...post,
            content: {
              ...post.content,
              likes: post.content.likes + 1
            }
          };
        }
        return post;
      })
    );
    
    return { success: true };
  };

  // Mark a review as helpful
  const markReviewAsHelpful = (reviewId) => {
    if (!isAuthenticated) return { success: false, message: 'You must be logged in' };
    
    setReviews(prevReviews => 
      prevReviews.map(review => {
        if (review.id === reviewId) {
          return { ...review, helpful: review.helpful + 1 };
        }
        return review;
      })
    );
    
    // Also update in posts
    setPosts(prevPosts => 
      prevPosts.map(post => {
        if (post.type === 'review' && post.content.id === reviewId) {
          return {
            ...post,
            content: {
              ...post.content,
              helpful: post.content.helpful + 1
            }
          };
        }
        return post;
      })
    );
    
    return { success: true };
  };

  // RSVP to an event
  const rsvpToEvent = (eventId, attending = true) => {
    if (!isAuthenticated) return { success: false, message: 'You must be logged in' };
    
    setEvents(prevEvents => 
      prevEvents.map(event => {
        if (event.id === eventId) {
          // In a real app, we would track which users are attending
          // Here we just increment or decrement the count
          return { 
            ...event, 
            attendees: attending 
              ? event.attendees + 1 
              : Math.max(0, event.attendees - 1) 
          };
        }
        return event;
      })
    );
    
    return { success: true };
  };

  // Get filtered content based on visibility and other criteria
  const getFilteredContent = (contentType, filters = {}) => {
    let result = [];
    
    switch (contentType) {
      case 'stories':
        result = stories.filter(story => {
          // Filter by visibility
          if (story.visibility === 'private') {
            return story.author.id === currentUser?.id;
          }
          if (story.visibility === 'community') {
            return isAuthenticated;
          }
          return true; // public stories
        });
        break;
        
      case 'reviews':
        result = reviews.filter(review => {
          // Filter by visibility
          if (review.visibility === 'private') {
            return review.author.id === currentUser?.id;
          }
          if (review.visibility === 'community') {
            return isAuthenticated;
          }
          return true; // public reviews
        });
        break;
        
      case 'events':
        result = events;
        break;
        
      case 'posts':
        result = posts.filter(post => {
          const content = post.content;
          // Filter by visibility
          if (content.visibility === 'private') {
            return content.author.id === currentUser?.id;
          }
          if (content.visibility === 'community') {
            return isAuthenticated;
          }
          return true; // public posts
        });
        break;
        
      default:
        break;
    }
    
    // Apply additional filters if provided
    if (filters.location) {
      result = result.filter(item => 
        item.location?.toLowerCase().includes(filters.location.toLowerCase())
      );
    }
    
    if (filters.tags && filters.tags.length > 0) {
      result = result.filter(item => 
        item.tags?.some(tag => filters.tags.includes(tag))
      );
    }
    
    return result;
  };

  // Value object to be provided to consumers
  const value = {
    stories,
    reviews,
    events,
    posts,
    loading,
    addStory,
    addReview,
    addEvent,
    toggleStoryLike,
    markReviewAsHelpful,
    rsvpToEvent,
    getFilteredContent
  };

  return <CommunityContext.Provider value={value}>{children}</CommunityContext.Provider>;
};

// Custom hook for using the community context
export const useCommunity = () => {
  const context = useContext(CommunityContext);
  if (context === undefined) {
    throw new Error('useCommunity must be used within a CommunityProvider');
  }
  return context;
};
